import { useEffect } from 'react';
import Helmet from 'react-helmet';
import { Routes, Route, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { ErrorBoundary } from '@sentry/react';
import {
  LANDING_PAGE,
  SEARCH,
  BOOKING,
  BOOKING_CHECK,
  BOOKING_CONFIRMATION,
  ABOUT,
  HOST,
  REVIEWS_NEW,
  REVIEWS_SUCCESS,
} from '../../../routes/config/pageTypes';
import { getPathForPage } from '../../../routes/config';
import I18n from '../../../config/i18n';
import HomeContainer from '../../../pages/home/containers/HomeContainer';
import LandingPageContainer from '../../../pages/landingPage/containers/LandingPageContainer';
import SearchContainer from '../../../pages/search/containers/SearchContainer';
import BookingContainer from '../../../pages/booking/containers/BookingContainer';
import BookingCheckContainer from '../../../pages/booking/containers/BookingCheckContainer';
import BookingConfirmationContainer from '../../../pages/booking/containers/BookingConfirmationContainer';
import About from '../../../pages/about/components/About';
import NotFound from '../../../pages/error/NotFound';
import HostContainer from '../../../pages/host/containers/HostContainer';
import ReviewsCreateContainer from '../../../pages/reviews/containers/ReviewCreateContainer';
import ReviewsSuccess from '../../../pages/reviews/components/ReviewsSuccess';
import Fa from '../../../utils/Fa';
import Header from '../../header/Header';
import Footer from '../../footer/Footer';
// import ChatTool from '../../../components/chatTool/components/ChatTool';
import HeaderAlert from '../../features/headerAlert/HeaderAlert';

import './app.css';

interface AppProps {
  isLoading: boolean;
  user: {
    // eslint-disable-next-line camelcase
    is_special: boolean;
    // eslint-disable-next-line camelcase
    is_admin: boolean;
    // eslint-disable-next-line camelcase
    is_translator: boolean;
    // eslint-disable-next-line camelcase
    is_agency_member: boolean;
    // eslint-disable-next-line camelcase
    is_host: boolean;
  };
  error: boolean;
  locale: string | undefined;
  setLocale: (locale: string) => void;
}

export default function App({
  isLoading,
  user,
  error,
  locale,
  setLocale,
}: AppProps): JSX.Element | null {
  const { lang } = useParams<'lang'>();

  useEffect(() => {
    if (locale !== lang) {
      setLocale(lang || I18n.defaultLocale);
    }
  }, [lang, locale, setLocale]);

  if (!locale) {
    return null;
  }
  I18n.setLocale(locale);

  return (
    <div className={classNames('casapilot-app', { loading: isLoading })}>
      <Header {...user} />
      <Helmet
        htmlAttributes={{ lang: I18n.locale }}
        defaultTitle="Casapilot"
        meta={[
          { name: 'keywords', content: I18n.t('meta.keywords') },
          { name: 'description', content: I18n.t('meta.description') },
        ]}
      />
      <div className="content">
        <HeaderAlert lang={locale} />
        {error ? <NotFound /> : null}

        <ErrorBoundary fallback={<NotFound />}>
          <Routes>
            <Route index element={<HomeContainer />} />
            <Route path={`${getPathForPage(LANDING_PAGE)}`} element={<LandingPageContainer />} />
            <Route path={`${getPathForPage(SEARCH)}`} element={<SearchContainer />} />
            <Route path={`${getPathForPage(BOOKING)}`} element={<BookingContainer />} />
            <Route path={`${getPathForPage(BOOKING_CHECK)}`} element={<BookingCheckContainer />} />
            <Route
              path={`${getPathForPage(BOOKING_CONFIRMATION)}`}
              element={<BookingConfirmationContainer />}
            />
            <Route path={`${getPathForPage(REVIEWS_NEW)}`} element={<ReviewsCreateContainer />} />
            <Route path={`${getPathForPage(REVIEWS_SUCCESS)}`} element={<ReviewsSuccess />} />
            <Route path={`${getPathForPage(ABOUT)}`} element={<About />} />
            <Route path={`${getPathForPage(HOST)}`} element={<HostContainer />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </div>
      <Footer />
      {/* }
      {cookieHasConsent ? <ChatTool widgetId={I18n.t('chat_tool.widget_id')} /> : null}
      { */}
      <div className="click-preventer">
        <Fa icon="refresh" className="fa-spin" />
      </div>
    </div>
  );
}
